import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { useTimeZoneStore } from '../store/useTimeZoneStore'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'
import { localHourMinuteLabel, localMonthApiRange, getOffsetMs, yesterdayStr } from '../utils/timeUtils'
import { calcTableRange, calcHlyTableRange } from './useGrafData'

function pad2(n) {
  return String(n).padStart(2, '0')
}

// Klouzavé okno posledních 30 dní končící VČEREJŠKEM pro "aktuální" — stejné jako useDlyOneMonthData
// (1 stanice). Dnešní den se nestahuje (den neskončil).
function calcDlyAktualRange() {
  const now = new Date()
  const rangeEnd = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1))
  const rangeStart = new Date(rangeEnd.getTime() - 30 * 86400000)
  return {
    begin_date: `${rangeStart.toISOString().slice(0, 10)}T00:00:00Z`,
    end_date: `${rangeEnd.toISOString().slice(0, 10)}T23:59:59Z`,
  }
}

// begin_date/end_date (ISO UTC) pro /daily_ts, TZ-aware podle granularity — stejný přístup jako
// useGrafTableData (10m)/useHlyOneDayData (hly)/useDlyOneMonthData (dly), aby se tabulka shodovala
// s grafem (ten server-side dělá stejný shift v ChartHandler.TimeZoneShift.toUtc).
function calcMeasTableRange(granKey, dateOrYearMonth, tz) {
  if (granKey === 'dly') {
    if (dateOrYearMonth.isAktual) return calcDlyAktualRange()
    const { year, month } = dateOrYearMonth
    const { begin_date, end_date } = localMonthApiRange(year, month, tz)
    // U aktuálního měsíce nekonči dál než včerejšek (dnešní denní data nejsou kompletní).
    const yEnd = `${yesterdayStr()}T23:59:59Z`
    return { begin_date, end_date: end_date > yEnd ? yEnd : end_date }
  }
  // 10m/hly — okno více dní (windowDays/alignEnd), případně "aktuální"
  const { dateStr, windowDays, alignEnd, isAktual } = dateOrYearMonth
  return granKey === 'hly'
    ? calcHlyTableRange(dateStr, windowDays, alignEnd, isAktual, tz)
    : calcTableRange(dateStr, windowDays, alignEnd, isAktual, tz)
}

// Tabulková data pro produkt "Měřené veličiny" — paralelní /daily_ts fetch přes stanice (na rozdíl
// od useGrafTableData, kde je vnější smyčka přes tsId a stanice je jedna, tady je 1 tsId a vnější
// smyčka je přes ghIds), viz doc/plans/station-group-meas.md sekce 5.
//
// Vrací:
// - dataMap: časový klíč (lokální) → { gh_id: value, ... }
// - labelMap: časový klíč → zobrazovaný label (lokální čas/den)
// - times: seřazené časové klíče
export function useMeasTableData(ghIds, tsId, granKey, dateOrYearMonth, enabled = true) {
  const tz = useTimeZoneStore(s => s.tz)

  const { begin_date: beginDate, end_date: endDate } = calcMeasTableRange(granKey, dateOrYearMonth, tz)

  const queryKey = ['measTableData', [...ghIds].sort(), tsId, beginDate, endDate, tz]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const responses = await fetchTsIds(
        ghIds,
        ghId => api.get(`/daily_ts?ts_id=${tsId}&gh_id=${ghId}&begin_date=${beginDate}&end_date=${endDate}`),
      )
      const result = Object.fromEntries(ghIds.map((ghId, i) => [ghId, responses[i]?.data ?? []]))
      const toFill = responses.flatMap(r => r?.to_fill ?? [])
      return { result, toFill }
    },
    enabled: ghIds.length > 0 && !!tsId && enabled,
    staleTime: 0,
  })

  useToFill(queryKey, data, (result, resp, item) => {
    const gh = item.params?.gh_id
    if (!gh || !(gh in result)) return result
    return { ...result, [gh]: resp.data }
  })

  // Klíč řádku se počítá z LOKÁLNÍHO času (stejně jako labelMap), ne z UTC — jinak by řazení a
  // seskupení řádků (hlavně kolem lokální půlnoci) neodpovídalo tomu, co ukazuje graf, když
  // tz != UTC (viz getOffsetMs).
  const dataMap = new Map()
  const labelMap = new Map()
  const dayMap = new Map()

  if (data?.result) {
    ghIds.forEach(ghId => {
      (data.result[ghId] ?? []).forEach(row => {
        if (row.dt == null) return
        const d = new Date(row.dt)
        const local = new Date(d.getTime() + getOffsetMs(d, tz))
        const key = granKey === 'dly'
          ? `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}`
          : `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())} ${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}`
        const entry = dataMap.get(key) ?? {}
        entry[ghId] = row.value ?? null
        dataMap.set(key, entry)
        if (!labelMap.has(key)) {
          labelMap.set(key, granKey === 'dly'
            ? (dateOrYearMonth.isAktual ? `${local.getUTCDate()}.${local.getUTCMonth() + 1}.` : `${local.getUTCDate()}.`)
            : localHourMinuteLabel(d, tz))
        }
        if (granKey !== 'dly' && !dayMap.has(key)) {
          dayMap.set(key, {
            dateStr: `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}`,
          })
        }
      })
    })
  }

  const times = Array.from(dataMap.keys()).sort()

  return { dataMap, labelMap, dayMap, times, isLoading, isError }
}
